/**
 * Booking Hooks
 *
 * Provides hooks for booking CRUD operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { BookingStatus as CalendarBookingStatus, SmallSuppliesSurcharge } from '@/types';
import type {
  WorkOrderCreateInput,
  WorkOrderCreateResult,
} from '@/components/workorders/workOrderCreateContract';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';
import { useQuoteMutationRequest } from './useQuoteMutationRequest';

// API types
// NOTE: 'confirmed' removed as a primary status (#534); use isConfirmed flag.
type BookingStatus =
  // Quotations (#1023) come back from the same list and are filterable (#2653).
  | 'quote'
  | 'quote_rejected'
  | 'pending'
  | 'vehicle_arrived'
  | 'in_progress'
  | 'waiting_approval'
  | 'completed'
  | 'cancelled';

export interface BookingListItem {
  workOrderNumber?: string | null;
  id: string;
  customerId: string;
  vehicleId: string;
  scheduledAt: string;
  vehicleDropoffAt?: string | null;
  customerRequestedPickupAt: string | null;
  estimatedDurationHours: number | null;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: BookingStatus;
  /** Quotation expiry (#849); null for non-quotes and open-ended quotes. */
  quoteExpiresAt?: string | null;
  customerNotes: string | null;
  internalNotes: string | null;
  managerNotes: string | null;
  assignedMechanicId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalCostCents: number | null;
  createdAt: string;
  updatedAt: string;
  customerName: string;
  customerPhone: string;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  mechanicName: string | null;
}

export interface BookingDetail {
  workOrderNumber?: string | null;
  id: string;
  customerId: string;
  vehicleId: string;
  scheduledAt: string;
  vehicleDropoffAt?: string | null;
  customerRequestedPickupAt: string | null;
  estimatedDurationHours: number | null;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: BookingStatus;
  quoteExpiresAt: string | null;
  customerNotes: string | null;
  internalNotes: string | null;
  managerNotes: string | null;
  assignedMechanicId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalCostCents: number | null;
  createdAt: string;
  updatedAt: string;
  customer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
  };
  vehicle: {
    id: string;
    registrationNumber: string;
    make: string | null;
    model: string | null;
    year: number | null;
  };
  mechanic: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface CalendarServiceAssignment {
  bookingServiceId: string;
  serviceId: string;
  serviceName: string;
  assignedMechanicId: string | null;
  mechanicName: string | null;
  status: string;
  sortOrder: number;
  scheduledAt: string | null;
  startedAt?: string | null;
  estimatedDurationHours: number | null;
  catalogDurationHours: number | null;
  actualDurationHours?: number;
  actualActiveHours?: number;
}

export interface CalendarBooking {
  workOrderNumber?: string | null;
  id: string;
  customerId: string;
  vehicleId: string;
  scheduledAt: string;
  /**
   * Imported history with a day but no clock time (#2760): the calendar lists
   * it per day instead of drawing it as a timed block at shift start.
   */
  scheduleIsDateOnly?: boolean;
  vehicleDropoffAt?: string | null;
  /** 'exact' pins the work to scheduledAt; 'floating' queues it within the day (#1139). */
  schedulingType?: 'exact' | 'floating';
  startedAt: string | null;
  estimatedDurationHours: number | null;
  actualDurationHours?: number;
  actualActiveHours?: number;
  unassignedActualDurationHours?: number;
  unassignedActualActiveHours?: number;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: BookingStatus;
  assignedMechanicId: string | null;
  mechanicScheduledStart: string | null;
  mechanicScheduledEnd: string | null;
  /** Calendar layout for this work order's services (#544). */
  serviceScheduling: 'sequential' | 'parallel';
  assignedLiftId: string | null;
  liftName: string | null;
  customerWaits: boolean;
  jointInspectionRequested: boolean;
  customerName: string;
  customerPhone: string | null;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  mechanicName: string | null;
  serviceAssignments: CalendarServiceAssignment[];
  /**
   * Derived approval wait (#3027): the work order has open approval requests.
   * Sits alongside the primary status; absent on payloads from older API pods.
   */
  isWaitingForApproval?: boolean;
  /** Number of open approval requests behind `isWaitingForApproval`; absent means 0. */
  openApprovalCount?: number;
}

export interface TentativeCalendarItem {
  id: string;
  bookingId: string;
  isTentative: true;
  scheduledAt: string;
  estimatedDurationHours: number;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: string;
  assignedMechanicId: string | null;
  customerName: string;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  mechanicName: string | null;
}

export interface CalendarWorkTimeEntry {
  id: string;
  bookingId: string;
  mechanicId: string;
  /** Null when the clocked row was never attributed to a service (#2705). */
  bookingServiceId: string | null;
  startedAt: string;
  endedAt: string | null;
  durationHours: string | null;
  breakHours: string;
  isActive: boolean;
  bookingStatus: CalendarBookingStatus;
}

export interface CalendarAbsence {
  /** Which kind of absence — the calendar labels the bar with it (#2261). */
  type: 'sick_leave' | 'vacation';
  employeeId: string;
  startDate: string;
  endDate: string | null;
}

export function useCalendarAbsences(startDate: string, endDate: string) {
  return useQuery({
    queryKey: [...queryKeys.bookings.all, 'unavailability', startDate, endDate],
    queryFn: () => api.get<{ sickLeaves: CalendarAbsence[]; vacations: CalendarAbsence[] }>(
      '/bookings/calendar/unavailability', { params: { startDate, endDate } },
    ),
    select: (data) => [...data.sickLeaves, ...data.vacations],
  });
}

export interface CalendarResponse {
  timeEntries?: CalendarWorkTimeEntry[];
  data: CalendarBooking[];
  tentativeItems: TentativeCalendarItem[];
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface BookingFilters {
  q?: string;
  status?: BookingStatus;
  mechanicId?: string;
  customerId?: string;
  /**
   * Party-native filters (#833 C2): work orders whose `orderer` party is this
   * person/company. Being the billing party or the pickup person on someone
   * else's work order does not match.
   */
  personId?: string;
  companyId?: string;
  vehicleId?: string;
  /** Inclusive lower bound on `scheduledAt` (ISO 8601). Matches GET /bookings `fromDate`. */
  fromDate?: string;
  /** Inclusive upper bound on `scheduledAt` (ISO 8601). Matches GET /bookings `toDate`. */
  toDate?: string;
  sortBy?: 'scheduledAt' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

/**
 * POST /bookings request — the work-order create contract (#3178). The mirror
 * is a copy of the shared Zod source; see workOrderCreateContract.ts.
 */
type CreateBookingData = WorkOrderCreateInput;

interface UpdateBookingData {
  scheduledAt?: string;
  /** Per-work-order small-supplies surcharge; null switches it off for this job (#2644). */
  smallSuppliesSurcharge?: SmallSuppliesSurcharge | null;
  vehicleDropoffAt?: string | null;
  /** Changing the type re-lays the mechanic's floating queue backend-side (#1137). */
  schedulingType?: 'exact' | 'floating';
  customerRequestedPickupAt?: string | null;
  estimatedDurationHours?: number;
  serviceType?: string;
  serviceIds?: string[];
  // Record keyed by bookingServiceId for surgical updates (no serviceIds), OR
  // an array parallel to serviceIds for the destructive replace path so duplicate
  // catalog services keep independent mechanic assignments (#418).
  serviceMechanics?: Record<string, string | null> | (string | null)[];
  /**
   * Ticked optional work steps per service, parallel to serviceIds (#3347).
   * A null entry, or no field at all, copies the catalog defaults.
   */
  serviceLaborItemIds?: (string[] | null)[];
  /**
   * Per-service schedule patches keyed by bookingServiceId.
   * `estimatedDurationHours` as a number latches plannedHoursManual; `null`
   * restores derived guidance time (#2667).
   */
  serviceSchedules?: Record<string, {
    scheduledAt?: string;
    estimatedDurationHours?: number | null;
  }>;
  customerNotes?: string;
  internalNotes?: string;
  managerNotes?: string;
  assignedMechanicId?: string | null;
  mileage?: number;
  contactPhone?: string | null;
  contactEmail?: string | null;
  customerWaits?: boolean;
  allowMinorMaintenance?: boolean;
  serviceScheduling?: 'sequential' | 'parallel';
  contactPersonId?: string | null;
  /** Registry person id for the primary_contact party (#833 B3); null clears it. */
  primaryContactPersonId?: string | null;
  customerId?: string;
  /** Move the work order to another vehicle; the server relinks the orderer and records history (#3285). */
  vehicleId?: string;
  /** Override the snapshotted hourly labor rate (integer cents) (#1437). */
  laborRateCents?: number;
  /** VAT-inclusive hourly rate the user typed (#1600). */
  laborRateGrossCents?: number;
}

type BookingConfirmationMethod = 'phone' | 'email' | 'in_person' | 'system';

interface UpdateStatusData {
  status: BookingStatus;
  confirmationMethod?: BookingConfirmationMethod;
}

interface ConfirmBookingInput {
  id: string;
  sendNotification?: boolean;
  /** How the customer confirmed. Omitted by callers that do not collect it. */
  confirmationMethod?: BookingConfirmationMethod;
}

export interface AcceptQuoteData {
  quoteVersionId?: string;
  scheduledAt: string;
  vehicleDropoffAt?: string | null;
  /** Exact time vs during-the-day (#1138); omitted keeps the stored type (#1931). */
  schedulingType?: 'exact' | 'floating';
  customerRequestedPickupAt?: string | null;
  /**
   * Overall planned hours for a no-service quote. Service-backed quotes omit
   * this and send `serviceSchedules` instead (#2667).
   */
  estimatedDurationHours?: number;
  /**
   * Per-service planned hours keyed by bookingServiceId (#2667). A positive
   * number latches plannedHoursManual. Accept-quote cannot restore derived
   * hours during the pre-accept lift collision check, so this payload is
   * number-only; restore stays on the post-accept schedule bar. Only changed
   * services are sent.
   */
  serviceSchedules?: Record<string, {
    estimatedDurationHours: number;
  }>;
  /** Booking-level mechanic/lift, one each for the whole work order (#2647). */
  assignedMechanicId?: string | null;
  assignedLiftId?: string | null;
  serviceMechanics?: Record<string, string | null>;
  serviceLifts?: Record<string, string | null>;
  customerWaits?: boolean;
  allowMinorMaintenance?: boolean;
  jointInspectionRequested?: boolean;
  preapprovedAdditionalWorkLimitCents?: number | null;
}

/**
 * Hook to fetch paginated list of bookings with infinite scroll
 */
export function useBookings(
  filters: BookingFilters = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.bookings.list({ ...filters }),
    queryFn: ({ pageParam }) => {
      const {
        q,
        status,
        mechanicId,
        customerId,
        personId,
        companyId,
        vehicleId,
        fromDate,
        toDate,
        sortBy,
        sortOrder,
        limit,
      } = filters;
      const params: Record<string, string | number | boolean | undefined> = {
        q,
        status,
        mechanicId,
        customerId,
        personId,
        companyId,
        vehicleId,
        fromDate,
        toDate,
        sortBy,
        sortOrder,
        limit,
        cursor: pageParam,
      };
      return api.get<PaginatedResponse<BookingListItem>>('/bookings', { params });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 15 * 1000, // 15 seconds - bookings change frequently
    enabled: options?.enabled ?? true,
  });
}

/** Filters for the page-number list. The infinite hook's cursor fields stay off this request. */
export interface BookingPageFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  status?: BookingStatus;
  mechanicId?: string;
  customerId?: string;
  personId?: string;
  companyId?: string;
  vehicleId?: string;
  fromDate?: string;
  toDate?: string;
}

interface OffsetBookingListResponse {
  data: BookingListItem[];
  pagination: { total: number };
}

/** Party sections offer a short page. The staff API still caps a page at 200. */
const BOOKING_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

/**
 * Page-number booking list (#2743). Always sends `limit` and `offset`
 * (including 0) so the route stays in offset mode, plus the list's existing
 * order (`scheduledAt` descending). `useBookings` is unchanged for callers
 * that still scroll.
 */
export function useBookingsPage(
  filters: BookingPageFilters = {},
  options?: { enabled?: boolean },
) {
  return usePagedQuery({
    queryKey: [...queryKeys.bookings.list({ ...filters }), 'offset'],
    filters,
    defaultRowsPerPage: 10,
    rowsPerPageOptions: BOOKING_PAGE_SIZE_OPTIONS,
    defaultSort: { sortBy: 'scheduledAt', sortDir: 'desc' },
    staleTime: 15 * 1000,
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
    fetchPage: ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: Record<string, string | number | boolean | undefined> = {
        ...pageFilters,
        limit,
        offset,
      };
      if (sortBy !== undefined) params.sortBy = sortBy;
      if (sortDir !== undefined) params.sortDir = sortDir;
      return api
        .get<OffsetBookingListResponse>('/bookings', { params })
        .then(fromOffsetResponse);
    },
  });
}

/**
 * Hook to fetch a single booking by ID
 */
export function useBooking(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bookings.detail(id ?? ''),
    queryFn: () => api.get<BookingDetail>(`/bookings/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch calendar bookings for a date range
 */
export function useCalendarBookings(startDate: string, endDate: string) {
  return useQuery({
    queryKey: queryKeys.bookings.calendar(startDate, endDate),
    queryFn: () =>
      api.get<CalendarResponse>('/bookings/calendar', {
        params: {
          startDate,
          endDate,
        },
      }),
    enabled: !!startDate && !!endDate,
    refetchInterval: 60_000, // Refresh actual work and timer state while the calendar is open.
    staleTime: 15 * 1000, // 15 seconds
    placeholderData: keepPreviousData, // Keep showing previous range while fetching new one
  });
}

/**
 * Hook to create a new booking
 */
export interface CreateBookingVariables {
  body: CreateBookingData;
  /**
   * One key per logical submit (#3160). A retry of the same submit sends the
   * same key, so a lost response replays the first result instead of creating
   * a second work order. The server keys it on the Idempotency-Key header.
   */
  idempotencyKey: string;
}

export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body, idempotencyKey }: CreateBookingVariables) =>
      api.post<WorkOrderCreateResult>('/bookings', body, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    onSuccess: () => {
      // Invalidate booking lists and calendar
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all,
        predicate: (query) =>
          query.queryKey[0] === 'bookings' &&
          (query.queryKey[1] === 'list' || query.queryKey[1] === 'calendar'),
      });
      // Invalidate work order lists (booking creates a work order)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
    },
  });
}

/**
 * Hook to update an existing booking
 */
export function useUpdateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingData }) =>
      api.patch<BookingDetail>(`/bookings/${id}`, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific booking, lists, and calendar
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.lists(),
      });
      // Also invalidate calendar view
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all,
        predicate: (query) =>
          query.queryKey[0] === 'bookings' && query.queryKey[1] === 'calendar',
      });
      // Invalidate work order detail/lists (booking and work order share the same ID)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoicePreview(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.history(id) });
    },
  });
}

interface AddBookingServiceResponse {
  id: string;
  bookingId: string;
  serviceId: string;
  assignedMechanicId: string | null;
  assignedLiftId: string | null;
  status: string;
  sortOrder: number;
}

/**
 * Invalidate every view that reflects a booking's services: the booking detail,
 * booking + work order lists, the calendar, the work order detail
 * (booking and work order share the same ID), and the invoice preview
 * whose lines follow the service set (#2643).
 */
function invalidateBookingServiceViews(queryClient: QueryClient, id: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.bookings.detail(id),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.bookings.lists(),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.bookings.all,
    predicate: (query) =>
      query.queryKey[0] === 'bookings' && query.queryKey[1] === 'calendar',
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.workOrders.detail(id),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.workOrders.lists(),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.invoicePreview(id),
  });
}

/**
 * Hook to add a single service (work) to an existing booking / work order.
 *
 * Uses the surgical additive endpoint (`POST /bookings/:id/services`) so existing
 * works keep their status and per-service progress. Adding via the destructive
 * `serviceIds` replace on useUpdateBooking would reset every sibling work's status (#860).
 */
export function useAddBookingService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      serviceId,
      mechanicId,
      liftId,
      laborItemIds,
    }: {
      id: string;
      serviceId: string;
      mechanicId?: string | null;
      liftId?: string | null;
      /** Ticked optional steps (#3347); omitted or null copies the catalog defaults. */
      laborItemIds?: string[] | null;
    }) =>
      api.post<AddBookingServiceResponse>(`/bookings/${id}/services`, {
        serviceId,
        ...(mechanicId !== undefined ? { mechanicId } : {}),
        ...(liftId !== undefined ? { liftId } : {}),
        ...(laborItemIds != null ? { laborItemIds } : {}),
      }),
    onSuccess: (_, { id }) => {
      invalidateBookingServiceViews(queryClient, id);
    },
  });
}

/**
 * Hook to remove a single service (work) from a booking / work order.
 *
 * Uses the surgical delete endpoint (`DELETE /bookings/:id/services/:bookingServiceId`)
 * so the remaining works keep their status and per-service progress. Removing via the
 * destructive `serviceIds` replace on useUpdateBooking would reset every kept work's
 * status (#885).
 */
export function useRemoveBookingService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, bookingServiceId }: { id: string; bookingServiceId: string }) =>
      api.delete(`/bookings/${id}/services/${bookingServiceId}`),
    onSuccess: (_, { id }) => {
      invalidateBookingServiceViews(queryClient, id);
    },
  });
}

/**
 * Hook to reorder the services (works) of a booking / work order.
 *
 * Uses the surgical reorder endpoint (`PATCH /bookings/:id/services/reorder`), which only
 * updates sort_order. Reordering via the destructive `serviceIds` replace on
 * useUpdateBooking would reset every work's status and per-service progress (#885).
 */
export function useReorderBookingServices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      orderedBookingServiceIds,
    }: {
      id: string;
      orderedBookingServiceIds: string[];
    }) =>
      api.patch(`/bookings/${id}/services/reorder`, { orderedBookingServiceIds }),
    onSuccess: (_, { id }) => {
      invalidateBookingServiceViews(queryClient, id);
    },
  });
}

/**
 * Hook to update booking status
 */
export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStatusData }) =>
      api.patch<BookingDetail>(`/bookings/${id}/status`, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific booking, lists, and calendar
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all,
        predicate: (query) =>
          query.queryKey[0] === 'bookings' && query.queryKey[1] === 'calendar',
      });
      // Booking and work order share the same ID
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
    },
  });
}

/**
 * Hook to accept a quote (#1023): saves the confirmed schedule and
 * transitions quote -> pending atomically via the dedicated endpoint.
 */
export function useAcceptQuote() {
  const queryClient = useQueryClient();
  const postQuote = useQuoteMutationRequest();

  return useMutation({
    mutationFn: ({ id, data, expectedUpdatedAt }: { id: string; data: AcceptQuoteData; expectedUpdatedAt: string }) =>
      postQuote<BookingDetail>(`/work-orders/${id}/accept-quote`, data, expectedUpdatedAt),
    onError: (error, { id }) => {
      if (error instanceof ApiError && error.status === 412) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate specific booking, lists, and calendar
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all,
        predicate: (query) =>
          query.queryKey[0] === 'bookings' && query.queryKey[1] === 'calendar',
      });
      // Booking and work order share the same ID
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
    },
  });
}

/**
 * Hook to confirm a booking via the dedicated /confirm endpoint (#534).
 * Sets isConfirmed=true without changing the primary operational status.
 * Passes confirmationMethod through when the staff member picked one (#2352).
 */
export function useConfirmBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sendNotification = true, confirmationMethod }: ConfirmBookingInput) =>
      api.post<{ id: string; status: string; confirmedAt: string }>(`/bookings/${id}/confirm`, {
        sendNotification,
        ...(confirmationMethod !== undefined ? { confirmationMethod } : {}),
      }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
    },
  });
}

/**
 * Hook to cancel a booking
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/bookings/${id}`),
    onSuccess: (_, id) => {
      // Invalidate specific booking, lists, and calendar
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all,
        predicate: (query) =>
          query.queryKey[0] === 'bookings' && query.queryKey[1] === 'calendar',
      });
      // Booking and work order share the same ID
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
    },
  });
}

// ============ Booking Skill Requirements ============

export interface BookingSkillRequirement {
  id: string;
  bookingId: string;
  skillId: string;
  minimumProficiency: 'beginner' | 'intermediate' | 'expert';
  isAutoSuggested: boolean;
  createdAt: string;
  skill: {
    id: string;
    name: string | null;
    type: string | null;
  };
}

/**
 * Hook to fetch skill requirements for a booking
 */
export function useBookingSkillRequirements(bookingId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bookings.skillRequirements(bookingId ?? ''),
    queryFn: () =>
      api.get<{ data: BookingSkillRequirement[] }>(
        `/bookings/${bookingId}/skill-requirements`
      ),
    enabled: !!bookingId,
    staleTime: 60 * 1000,
    select: (data) => data.data,
  });
}

interface AddSkillRequirementData {
  skillId: string;
  minimumProficiency?: 'beginner' | 'intermediate' | 'expert';
  isAutoSuggested?: boolean;
}

/**
 * Hook to add a skill requirement to a booking
 */
export function useAddBookingSkillRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      data,
    }: {
      bookingId: string;
      data: AddSkillRequirementData;
    }) =>
      api.post<BookingSkillRequirement>(
        `/bookings/${bookingId}/skill-requirements`,
        data
      ),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.skillRequirements(bookingId),
      });
    },
  });
}

/**
 * Hook to remove a skill requirement from a booking
 */
export function useRemoveBookingSkillRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, skillId }: { bookingId: string; skillId: string }) =>
      api.delete(`/bookings/${bookingId}/skill-requirements/${skillId}`),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.skillRequirements(bookingId),
      });
    },
  });
}

// ============ Lift Assignment ============

interface AssignLiftResponse {
  id: string;
  assignedLiftId: string | null;
  lift?: {
    id: string;
    name: string;
    identifier: string;
    type: string;
  };
}

/**
 * Hook to assign a lift to a booking
 */
export function useAssignLiftToBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      liftId,
    }: {
      bookingId: string;
      liftId: string | null;
    }) =>
      api.patch<AssignLiftResponse>(`/bookings/${bookingId}/assign-lift`, {
        liftId,
      }),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(bookingId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.resourceValidation(bookingId),
      });
    },
  });
}

// ============ Resource Validation ============

export interface ResourceValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  summary: {
    hasRequiredSkills: number;
    hasMechanicAssigned: boolean;
    hasLiftAssigned: boolean;
  };
}

/**
 * Hook to validate booking resources (mechanic skills + lift availability)
 */
export function useValidateBookingResources(bookingId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bookings.resourceValidation(bookingId ?? ''),
    queryFn: () =>
      api.post<ResourceValidationResult>(
        `/bookings/${bookingId}/validate-resources`
      ),
    enabled: !!bookingId,
    staleTime: 30 * 1000, // Re-validate every 30 seconds
  });
}

/**
 * Hook to manually trigger resource validation
 */
export function useValidateBookingResourcesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookingId: string) =>
      api.post<ResourceValidationResult>(
        `/bookings/${bookingId}/validate-resources`
      ),
    onSuccess: (data, bookingId) => {
      // Update the cache with new validation result
      queryClient.setQueryData(
        queryKeys.bookings.resourceValidation(bookingId),
        data
      );
    },
  });
}
